import { watch, whenOnce } from '@arcgis/core/core/reactiveUtils';
import MapView from '@arcgis/core/views/MapView';
import Graphic from '@arcgis/core/Graphic';
import Extent from '@arcgis/core/geometry/Extent';
import { debounce } from 'lodash';
import LocalStoragePreferences from '../../../utils/local-storage-preferences';
import { FULCRUM_DECK_LAYER_ID } from '../arc-gis-map-view';
import { GeometricShape } from '../../../types/geo';
import { buildGraphic } from '../../../sketch-editor/graphics-handler';

export const DEFAULT_ZOOM_LEVEL = 17;
export const WORLD_ZOOM_LEVEL = 3;

// FeatureFocusTracker set focus and tracks extent changes, similar to the UrlAndAppPrefExtents
// plugin, but for the Record Editor and Repeatable List Views (rather that the Data Viewer).
const FeatureFocusTracker = (graphic: Graphic | undefined, preferences: LocalStoragePreferences) => {
  const DEBOUNCE_INTERVAL = 50;

  const debounceExtentUpdate = debounce(
    (mapView: MapView) => {
      preferences?.setLastEditorMapExtents(mapView.extent);
    },
    DEBOUNCE_INTERVAL,
    { leading: true },
  );

  const watchForExtentChanges = (mapView: MapView) => {
    watch(
      () => mapView?.extent,
      () => {
        if (!mapView.updating && mapView.extent) {
          debounceExtentUpdate(mapView);
        }
      },
    );
  };

  const watchForZoomChanges = (mapView: MapView) => {
    watch(
      () => mapView?.zoom,
      () => {
        if (mapView?.ready) {
          debounceExtentUpdate(mapView);
        }
      },
    );
  };

  interface GoToTarget {
    target: Graphic | Extent;
    zoom?: number;
  }

  const goToDataViewerExtent = (mapView: MapView) => {
    const { lastMapExtents } = preferences ?? {};

    if (!lastMapExtents) {
      mapView.goTo({ zoom: WORLD_ZOOM_LEVEL });
      return;
    }

    const { longitude, latitude, zoom } = lastMapExtents;
    const geometricShape: GeometricShape = { type: 'Point', coordinates: [longitude, latitude] };
    const goToGraphic = buildGraphic(geometricShape);
    mapView.goTo({ target: goToGraphic, zoom });
  };

  return {
    initMap: async (mapView: MapView) => {
      const deckLayer = mapView.map.findLayerById(FULCRUM_DECK_LAYER_ID);

      const layerView = await mapView.whenLayerView(deckLayer);
      await whenOnce(() => !layerView.updating);
      watchForExtentChanges(mapView);
      watchForZoomChanges(mapView);
    },
    extendMap: (mapView: MapView) => {
      const { lastEditorMapExtents } = preferences ?? {};

      if (!graphic && !lastEditorMapExtents) {
        goToDataViewerExtent(mapView);
        return;
      }

      const goTo: GoToTarget = { target: graphic ?? lastEditorMapExtents };
      if (!graphic || graphic?.geometry?.type === 'point') {
        goTo.zoom = DEFAULT_ZOOM_LEVEL;
      }

      mapView.goTo(goTo);
    },
  };
};

export default FeatureFocusTracker;
